import React from "react";
import { Button, Form, InputNumber, Modal, Switch, message } from "antd";
import { ClientWithStorage } from "~/services/client/client.queries";
import { StorageSettingsValues } from "../types";
import { updateClientStorageSettings } from "~/services/storage/storage.api";

interface StorageSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  selectedClient: ClientWithStorage | null;
  form: any;
  onSuccess: () => void;
}

export const StorageSettingsModal: React.FC<StorageSettingsModalProps> = ({
  visible,
  onClose,
  selectedClient,
  form,
  onSuccess,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleSaveSettings = async (values: StorageSettingsValues) => {
    try {
      setIsProcessing(true);

      if (!selectedClient) {
        message.error("No client selected");
        return;
      }

      // Convert insurance rate from percentage to decimal
      const insuranceRate = values.includeInsurance
        ? values.insuranceRate / 100
        : undefined;

      // Update client storage settings
      await updateClientStorageSettings(selectedClient.id, {
        storageRate: values.storageRate,
        includeInsurance: values.includeInsurance,
        insuranceRate,
      });

      message.success("Storage settings updated");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating storage settings:", error);
      message.error("Failed to update storage settings");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!selectedClient) return null;

  return (
    <Modal
      title={`Storage Settings - ${selectedClient.companyName}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => form.submit()}
          loading={isProcessing}
        >
          Save Settings
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleSaveSettings}>
        <Form.Item
          name="storageRate"
          label="Storage Rate ($ per cubic foot)"
          rules={[
            { required: true, message: "Please enter the storage rate" },
            {
              type: "number",
              min: 0,
              message: "Rate must be a positive number",
            },
          ]}
        >
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            prefix="$"
          />
        </Form.Item>

        <Form.Item
          name="includeInsurance"
          label="Include Insurance"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="insuranceRate"
          label="Insurance Rate (% of declared value)"
          rules={[
            { required: true, message: "Please enter the insurance rate" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Rate must be between 0 and 100%",
            },
          ]}
          dependencies={["includeInsurance"]}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.1}
            precision={1}
            style={{ width: "100%" }}
            suffix="%"
            disabled={!form.getFieldValue("includeInsurance")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 